import { type ImportColumnDescriptor, type ImportRowItem } from '@utazzo/shared';
import { UzAlert, UzBadge, UzButton, UzInput, UzSelect } from '@utazzo/ui';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  RotateCcw,
  X,
} from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { importedSnapshot, stripImportMeta } from '../lead-import-preview';
import { usePatchImportRow, useSkipImportRow } from '../use-imports';

import { ImportLookupCell } from './ImportLookupCell';

/** Companion `fixedData` id field for each import lookup name column. */
const LOOKUP_ID_FIELD: Partial<Record<string, string>> = {
  departureCityName: 'departureCityId',
  destinationCityName: 'destinationCityId',
  assignedAgentName: 'assignedAgentId',
  createdByName: 'createdById',
  queryTypeName: 'queryTypeId',
  querySourceName: 'querySourceId',
  queryMediumName: 'queryMediumId',
};

const applyLookupEdit = (
  prev: Record<string, string>,
  field: string,
  value: string,
  pickedId?: string,
): Record<string, string> => {
  const next = { ...prev, [field]: value };
  const idField = LOOKUP_ID_FIELD[field];
  if (!idField) return next;
  if (pickedId) next[idField] = pickedId;
  else delete next[idField];
  return next;
};

/**
 * One import row, collapsed by default with a compact summary. Used by both
 * the Lead importer (single sheet, `sheetKey=""`) and the Hotels importer
 * (multi-sheet, `sheetKey="Hotels"|"Rooms"|…`). The `summaryFields` prop
 * lets each caller pick the 1-3 fields shown in the collapsed header so the
 * row stays scannable for the entity in question.
 */
interface RowCardProps {
  row: ImportRowItem;
  columns: ReadonlyArray<ImportColumnDescriptor>;
  jobId: string;
  /** Empty string for single-sheet importers; the sheet name for multi. */
  sheetKey?: string;
  /** Field ids whose merged value is shown in the collapsed header. */
  summaryFields?: ReadonlyArray<string>;
  /** Per-row column filter (lead import trims empty / legacy fields). */
  filterColumns?: (
    columns: ReadonlyArray<ImportColumnDescriptor>,
    merged: Record<string, unknown>,
  ) => ReadonlyArray<ImportColumnDescriptor>;
}

export const RowCard = ({
  row,
  columns,
  jobId,
  sheetKey = '',
  summaryFields,
  filterColumns,
}: RowCardProps): React.JSX.Element => {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState(false);
  const patch = usePatchImportRow(jobId);
  const skip = useSkipImportRow(jobId);
  const isInvalid = row.status === 'INVALID';
  const isSkipped = row.status === 'SKIPPED';
  const isCommitted = row.status === 'COMMITTED';
  const isFailed = row.status === 'COMMIT_FAILED';
  const errorByColumn = new Map<string, string>(
    (row.validationErrors ?? []).map((e) => [e.column, e.message]),
  );
  const imported = importedSnapshot(row.rawData as Record<string, unknown>);
  const merged: Record<string, unknown> = {
    ...stripImportMeta(row.rawData as Record<string, unknown>),
    ...((row.fixedData ?? {}) as Record<string, unknown>),
  };
  const visibleColumns = filterColumns ? filterColumns(columns, merged) : columns;
  const summary = (summaryFields ?? [])
    .map((f) => valueOf(merged, f))
    .filter(Boolean)
    .join(' · ');
  const errorCount = (row.validationErrors ?? []).length;
  // Errors whose column is not one of the rendered fields would otherwise be
  // invisible - the inline per-field error never mounts. A row-level error on a
  // key that isn't shown (a wrong meal plan, a room key missing from another
  // sheet, a stale/mismatched column set) still gets surfaced, so "1 error" is
  // never a badge with nothing behind it.
  const visibleFields = new Set(visibleColumns.map((c) => c.field));
  const unmappedErrors = (row.validationErrors ?? []).filter((e) => !visibleFields.has(e.column));

  const saveFixes = async (): Promise<void> => {
    if (Object.keys(edits).length === 0) return;
    await patch.mutateAsync({ sheetKey, rowIndex: row.rowIndex, fixedCells: edits });
    setEdits({});
  };

  return (
    <div
      className={`rounded-md border ${
        isInvalid
          ? 'border-amber-200 bg-amber-50/40'
          : isFailed
            ? 'border-red-200 bg-red-50/40'
            : isCommitted
              ? 'border-emerald-200 bg-emerald-50/40'
              : isSkipped
                ? 'border-zinc-200 bg-zinc-50/60'
                : 'border-zinc-200 bg-white'
      }`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-left hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:outline-none"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-zinc-500" />
          )}
          <span className="text-sm font-medium">Row {row.rowIndex}</span>
          <RowStatusBadge status={row.status} />
          {!expanded && summary && (
            <span className="truncate text-xs text-zinc-600">{summary}</span>
          )}
          {!expanded && isInvalid && errorCount > 0 && (
            <span className="text-xs font-medium text-amber-700">
              {errorCount} error{errorCount === 1 ? '' : 's'}
            </span>
          )}
          {isCommitted && row.entityId && (
            <Link
              to={`/leads/${row.entityId}`}
              className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Open lead <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </button>
        {!isCommitted && (
          <button
            type="button"
            onClick={() => skip.mutateAsync({ sheetKey, rowIndex: row.rowIndex, skip: !isSkipped })}
            disabled={skip.isPending}
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:outline-none disabled:opacity-50"
          >
            {isSkipped ? (
              <>
                <RotateCcw className="h-3.5 w-3.5" />
                Un-skip
              </>
            ) : (
              <>
                <X className="h-3.5 w-3.5" />
                Skip row
              </>
            )}
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-current/10 px-3 py-3">
          {row.errorMessage && (
            <UzAlert tone="destructive" className="mb-2">
              <AlertCircle className="mr-1 inline h-3.5 w-3.5" />
              {row.errorMessage}
            </UzAlert>
          )}
          {unmappedErrors.length > 0 && (
            <UzAlert tone="destructive" className="mb-2">
              <div className="flex items-start gap-1">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <ul className="space-y-0.5 text-xs">
                  {unmappedErrors.map((e, i) => (
                    <li key={`${e.column}-${i}`}>
                      <span className="font-medium">{e.column}:</span> {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            </UzAlert>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {visibleColumns.map((col) => {
              const value = merged[col.field];
              const errorMessage = errorByColumn.get(col.field);
              const showStr =
                value === null || value === undefined
                  ? ''
                  : typeof value === 'object'
                    ? JSON.stringify(value)
                    : String(value);
              const sheetLiteral =
                imported[col.field] ?? (row.rawData as Record<string, unknown>)?.[col.field];
              const rawStr =
                sheetLiteral === null || sheetLiteral === undefined
                  ? ''
                  : typeof sheetLiteral === 'object'
                    ? JSON.stringify(sheetLiteral)
                    : String(sheetLiteral);
              const rawDisplay = col.type === 'date' ? toDateInputValue(sheetLiteral) : rawStr;
              const edited = edits[col.field] !== undefined && edits[col.field] !== showStr;
              const showHintBelow = col.hint && !errorMessage && col.type !== 'lookup';
              return (
                <div key={col.field} className="space-y-1">
                  <label className="text-xs font-medium text-zinc-700">{col.label}</label>
                  {rawDisplay && !isCommitted && (
                    <div className="text-[11px] text-zinc-500">
                      Imported:{' '}
                      <span
                        className={`font-mono ${
                          edited ? 'text-zinc-500 line-through' : 'text-zinc-700'
                        }`}
                      >
                        {rawDisplay}
                      </span>
                    </div>
                  )}
                  {isCommitted ? (
                    <div className="rounded border border-zinc-200 bg-white px-2 py-1 text-sm text-zinc-700">
                      {showStr || '-'}
                    </div>
                  ) : col.type === 'lookup' && col.lookup ? (
                    <ImportLookupCell
                      name={`${row.rowIndex}-${col.field}`}
                      kind={col.lookup}
                      value={edits[col.field] ?? showStr}
                      importedValue={rawStr}
                      error={errorMessage}
                      hint={col.hint ?? undefined}
                      onChange={(v, pickedId) =>
                        setEdits((prev) => applyLookupEdit(prev, col.field, v, pickedId))
                      }
                    />
                  ) : col.type === 'select' && col.options && col.options.length > 0 ? (
                    <UzSelect
                      name={`${row.rowIndex}-${col.field}`}
                      options={col.options}
                      placeholder={
                        showStr ? `"${showStr}" not in master - pick from list` : 'Pick from list'
                      }
                      value={(() => {
                        const cur = edits[col.field] ?? showStr;
                        return col.options!.some((o) => o.value === cur) ? cur : undefined;
                      })()}
                      onValueChange={(v) => setEdits((prev) => ({ ...prev, [col.field]: v ?? '' }))}
                      error={errorMessage}
                    />
                  ) : (
                    <UzInput
                      name={`${row.rowIndex}-${col.field}`}
                      type={
                        col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'
                      }
                      value={
                        col.type === 'date'
                          ? toDateInputValue(edits[col.field] ?? value)
                          : (edits[col.field] ?? showStr)
                      }
                      onChange={(e) =>
                        setEdits((prev) => ({ ...prev, [col.field]: e.target.value }))
                      }
                      error={errorMessage}
                    />
                  )}
                  {showHintBelow && <p className="text-[11px] text-zinc-500">{col.hint}</p>}
                </div>
              );
            })}
          </div>

          {Object.keys(edits).length > 0 && !isCommitted && (
            <div className="mt-3 flex justify-end gap-2">
              <UzButton size="sm" variant="ghost" onClick={() => setEdits({})}>
                Discard edits
              </UzButton>
              <UzButton size="sm" onClick={saveFixes} loading={patch.isPending}>
                <Check className="mr-1 h-3.5 w-3.5" />
                Save + re-validate
              </UzButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RowStatusBadge = ({ status }: { status: ImportRowItem['status'] }): React.JSX.Element => {
  const cfg: Record<
    ImportRowItem['status'],
    { tone: 'success' | 'destructive' | 'warning' | 'secondary' | 'default'; label: string }
  > = {
    INVALID: { tone: 'warning', label: 'Invalid' },
    VALID: { tone: 'success', label: 'Valid' },
    SKIPPED: { tone: 'secondary', label: 'Skipped' },
    COMMITTED: { tone: 'success', label: 'Committed' },
    COMMIT_FAILED: { tone: 'destructive', label: 'Failed' },
  };
  const c = cfg[status];
  return <UzBadge tone={c.tone}>{c.label}</UzBadge>;
};

function valueOf(merged: Record<string, unknown>, key: string): string {
  const v = merged[key];
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v).trim();
}

/**
 * Normalize anything a `type: 'date'` column might carry into the
 * `YYYY-MM-DD` form an `<input type="date">` accepts.
 */
function toDateInputValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  const ymd = match?.[1];
  if (ymd) return ymd;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
